/* ==========================================================================
   Rook UI Core — Scroll Area Controller
   Web Component: <rk-scroll-area>
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkScrollArea extends RkComponent {
    static hydration = 'idle';

    #dragging = null;
    #dragStart = 0;
    #scrollStart = 0;
    #hideTimer = null;
    #resizeObserver = null;
    #onResize = null;
    #onScroll = null;
    #dragController = null;

    setup() {
        this.viewport = this.querySelector('.rk-scroll-area__viewport');
        this.trackY = this.querySelector('.rk-scroll-area__scrollbar--vertical');
        this.thumbY = this.trackY?.querySelector('.rk-scroll-area__thumb');
        this.trackX = this.querySelector('.rk-scroll-area__scrollbar--horizontal');
        this.thumbX = this.trackX?.querySelector('.rk-scroll-area__thumb');
        this.corner = this.querySelector('.rk-scroll-area__corner');

        if (!this.viewport) return;

        const visibility = this.dataset.visibility || 'auto';

        const delegateMap = {
            'pointerdown .rk-scroll-area__scrollbar--vertical': this.#onTrackOrThumbY,
            'pointerdown .rk-scroll-area__scrollbar--horizontal': this.#onTrackOrThumbX,
        };
        if (visibility === 'hover') {
            delegateMap['pointerenter'] = () => this.#showScrollbars();
            delegateMap['pointerleave'] = () => this.#scheduleHide();
        }
        this.delegate(delegateMap);

        // Scroll listener: direct attach for passive:true performance.
        this.#onScroll = () => this.#handleScroll();
        this.viewport.addEventListener('scroll', this.#onScroll, { passive: true });

        // Window resize (debounced).
        this.#onResize = this.#debounce(() => this.#recalculate(), 100);
        window.addEventListener('resize', this.#onResize);

        // ResizeObserver for dynamic content.
        this.#resizeObserver = new ResizeObserver(() => this.#recalculate());
        this.#resizeObserver.observe(this.viewport);
        const content = this.viewport.firstElementChild;
        if (content) {
            this.#resizeObserver.observe(content);
        }

        this.#recalculate();
    }

    teardown() {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;

        if (this.#onScroll && this.viewport) {
            this.viewport.removeEventListener('scroll', this.#onScroll);
            this.#onScroll = null;
        }
        if (this.#onResize) {
            window.removeEventListener('resize', this.#onResize);
            this.#onResize = null;
        }
        this.#dragController?.abort();
        this.#dragController = null;
        if (this.#hideTimer) {
            clearTimeout(this.#hideTimer);
            this.#hideTimer = null;
        }
    }

    #handleScroll() {
        this.#syncThumbs();
        const visibility = this.dataset.visibility || 'auto';
        if (visibility === 'auto') {
            this.#showScrollbars();
            this.#scheduleHide();
        }
    }

    #onTrackOrThumbY(event) {
        if (event.target.closest('.rk-scroll-area__thumb')) {
            this.#onPointerDown(event, 'y');
        } else if (event.target === this.trackY) {
            this.#onTrackClick(event, 'y');
        }
    }

    #onTrackOrThumbX(event) {
        if (event.target.closest('.rk-scroll-area__thumb')) {
            this.#onPointerDown(event, 'x');
        } else if (event.target === this.trackX) {
            this.#onTrackClick(event, 'x');
        }
    }

    #onPointerDown(event, axis) {
        event.preventDefault();
        event.stopPropagation();
        this.#dragging = axis;

        if (axis === 'y') {
            this.#dragStart = event.clientY;
            this.#scrollStart = this.viewport.scrollTop;
            this.thumbY.classList.add('rk-scroll-area__thumb--active');
        } else {
            this.#dragStart = event.clientX;
            this.#scrollStart = this.viewport.scrollLeft;
            this.thumbX.classList.add('rk-scroll-area__thumb--active');
        }

        this.#dragController?.abort();
        this.#dragController = new AbortController();
        const signal = this.#dragController.signal;

        document.addEventListener('pointermove', (e) => this.#onPointerMove(e), { signal });
        document.addEventListener('pointerup', () => this.#onPointerUp(), { signal });
    }

    #onPointerMove(event) {
        if (!this.#dragging || !this.viewport) return;
        const vp = this.viewport;

        if (this.#dragging === 'y') {
            const delta = event.clientY - this.#dragStart;
            const scrollableY = vp.scrollHeight - vp.clientHeight;
            const trackHeight = this.trackY.clientHeight;
            const thumbHeight = this.thumbY.offsetHeight;
            const scrollRatio = scrollableY / (trackHeight - thumbHeight);
            vp.scrollTop = this.#scrollStart + delta * scrollRatio;
        } else {
            const delta = event.clientX - this.#dragStart;
            const scrollableX = vp.scrollWidth - vp.clientWidth;
            const trackWidth = this.trackX.clientWidth;
            const thumbWidth = this.thumbX.offsetWidth;
            const scrollRatio = scrollableX / (trackWidth - thumbWidth);
            vp.scrollLeft = this.#scrollStart + delta * scrollRatio;
        }
    }

    #onPointerUp() {
        this.thumbY?.classList.remove('rk-scroll-area__thumb--active');
        this.thumbX?.classList.remove('rk-scroll-area__thumb--active');
        this.#dragging = null;
        this.#dragController?.abort();
        this.#dragController = null;
    }

    #onTrackClick(event, axis) {
        if (!this.viewport) return;
        const vp = this.viewport;

        if (axis === 'y') {
            const trackRect = this.trackY.getBoundingClientRect();
            const clickRatio = (event.clientY - trackRect.top) / trackRect.height;
            vp.scrollTop = clickRatio * (vp.scrollHeight - vp.clientHeight);
        } else {
            const trackRect = this.trackX.getBoundingClientRect();
            const clickRatio = (event.clientX - trackRect.left) / trackRect.width;
            vp.scrollLeft = clickRatio * (vp.scrollWidth - vp.clientWidth);
        }
    }

    #recalculate() {
        if (!this.viewport) return;

        const vp = this.viewport;
        const hasOverflowY = vp.scrollHeight > vp.clientHeight;
        const hasOverflowX = vp.scrollWidth > vp.clientWidth;

        if (this.trackY) {
            this.trackY.classList.toggle('rk-scroll-area__scrollbar--hidden', !hasOverflowY);
        }
        if (this.trackX) {
            this.trackX.classList.toggle('rk-scroll-area__scrollbar--hidden', !hasOverflowX);
        }
        if (this.corner) {
            this.corner.classList.toggle('rk-scroll-area__corner--hidden', !(hasOverflowY && hasOverflowX));
        }

        if (this.thumbY && hasOverflowY) {
            const ratio = vp.clientHeight / vp.scrollHeight;
            const trackHeight = this.trackY.clientHeight;
            const thumbHeight = Math.max(ratio * trackHeight, 24);
            this.thumbY.style.height = `${thumbHeight}px`;
        }

        if (this.thumbX && hasOverflowX) {
            const ratio = vp.clientWidth / vp.scrollWidth;
            const trackWidth = this.trackX.clientWidth;
            const thumbWidth = Math.max(ratio * trackWidth, 24);
            this.thumbX.style.width = `${thumbWidth}px`;
        }

        this.#syncThumbs();
    }

    #syncThumbs() {
        if (!this.viewport) return;
        const vp = this.viewport;

        if (this.thumbY && this.trackY) {
            const scrollableY = vp.scrollHeight - vp.clientHeight;
            if (scrollableY > 0) {
                const ratio = vp.scrollTop / scrollableY;
                const trackHeight = this.trackY.clientHeight;
                const thumbHeight = this.thumbY.offsetHeight;
                const maxOffset = trackHeight - thumbHeight;
                this.thumbY.style.transform = `translateY(${ratio * maxOffset}px)`;
            }
        }

        if (this.thumbX && this.trackX) {
            const scrollableX = vp.scrollWidth - vp.clientWidth;
            if (scrollableX > 0) {
                const ratio = vp.scrollLeft / scrollableX;
                const trackWidth = this.trackX.clientWidth;
                const thumbWidth = this.thumbX.offsetWidth;
                const maxOffset = trackWidth - thumbWidth;
                this.thumbX.style.transform = `translateX(${ratio * maxOffset}px)`;
            }
        }
    }

    #showScrollbars() {
        clearTimeout(this.#hideTimer);
        this.classList.add('rk-scroll-area--scrolling');
    }

    #scheduleHide() {
        clearTimeout(this.#hideTimer);
        this.#hideTimer = setTimeout(() => {
            this.classList.remove('rk-scroll-area--scrolling');
        }, 1200);
    }

    #debounce(fn, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    }
}

if (!customElements.get('rk-scroll-area')) {
    customElements.define('rk-scroll-area', RkScrollArea);
}
